import React, { createContext, useContext, useState, useEffect } from 'react'
import sliderService from '../api/sliderService'

const SliderContext = createContext()

export const SliderProvider = ({ children }) => {
  const [sliderItems, setSliderItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Fetch all slides on mount
  useEffect(() => {
    let isMounted = true;
    
    const loadSlides = async () => {
      try {
        setLoading(true)
        const response = await sliderService.getAllSlides()
        if (isMounted) {
          setSliderItems(response.data.slides || [])
          setError(null)
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.error('Error fetching slides:', err)
        }
        if (isMounted) {
          setError(err.response?.data?.message || 'Failed to load slides')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadSlides()

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [])

  const fetchSlides = async () => {
    try {
      setLoading(true)
      const response = await sliderService.getAllSlides()
      setSliderItems(response.data.slides || [])
      setError(null)
    } catch (err) {
      console.error('Error fetching slides:', err)
      setError(err.response?.data?.message || 'Failed to load slides')
    } finally {
      setLoading(false)
    }
  }

  // Get active slides for display
  const getActiveSlides = async () => {
    try {
      const response = await sliderService.getActiveSlides()
      return response.data.slides || []
    } catch (err) {
      console.error('Error fetching active slides:', err)
      return sliderItems.filter(slide => slide.isActive).sort((a, b) => a.order - b.order)
    }
  }

  // Add new slide
  const addSlide = async (formData) => {
    try {
      const response = await sliderService.createSlide(formData)
      setSliderItems(prev => [...prev, response.data.slide])
      return { success: true, data: response.data.slide }
    } catch (err) {
      console.error('Error creating slide:', err)
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to create slide' 
      }
    }
  }

  // Update existing slide
  const updateSlide = async (slideId, formData) => {
    try {
      const response = await sliderService.updateSlide(slideId, formData)
      setSliderItems(prev => prev.map(slide => 
        slide._id === slideId ? response.data.slide : slide
      ))
      return { success: true, data: response.data.slide }
    } catch (err) {
      console.error('Error updating slide:', err)
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to update slide' 
      }
    }
  }

  // Delete slide
  const deleteSlide = async (slideId) => {
    try {
      await sliderService.deleteSlide(slideId)
      setSliderItems(prev => prev.filter(slide => slide._id !== slideId))
      return { success: true }
    } catch (err) {
      console.error('Error deleting slide:', err)
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to delete slide' 
      }
    }
  }

  // Delete multiple slides
  const deleteSlides = async (slideIds) => {
    try {
      // Delete each slide individually
      await Promise.all(slideIds.map(id => sliderService.deleteSlide(id)))
      setSliderItems(prev => prev.filter(slide => !slideIds.includes(slide._id)))
      return { success: true }
    } catch (err) {
      console.error('Error deleting slides:', err)
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to delete slides' 
      }
    }
  }

  // Toggle slide active status
  const toggleSlideActive = async (slideId) => {
    try {
      const slide = sliderItems.find(s => s._id === slideId)
      if (!slide) return { success: false, message: 'Slide not found' }

      const formData = new FormData()
      formData.append('isActive', !slide.isActive)

      const response = await sliderService.updateSlide(slideId, formData)
      setSliderItems(prev => prev.map(s => 
        s._id === slideId ? response.data.slide : s
      ))
      return { success: true }
    } catch (err) {
      console.error('Error toggling slide:', err)
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to toggle slide' 
      }
    }
  }

  // Reorder slides
  const moveSlide = async (slideId, direction) => {
    try {
      const currentIndex = sliderItems.findIndex(slide => slide._id === slideId)
      if (currentIndex === -1) return { success: false, message: 'Slide not found' }

      let newOrder = sliderItems[currentIndex].order
      if (direction === 'up' && currentIndex > 0) {
        newOrder = sliderItems[currentIndex - 1].order
      } else if (direction === 'down' && currentIndex < sliderItems.length - 1) {
        newOrder = sliderItems[currentIndex + 1].order
      }

      const formData = new FormData()
      formData.append('order', newOrder)

      const response = await sliderService.updateSlide(slideId, formData)
      await fetchSlides() // Refresh all slides to get updated order
      return { success: true }
    } catch (err) {
      console.error('Error reordering slide:', err)
      return { 
        success: false, 
        message: err.response?.data?.message || 'Failed to reorder slide' 
      }
    }
  }

  // Get slide statistics
  const getStats = () => {
    const total = sliderItems.length
    const active = sliderItems.filter(slide => slide.isActive).length
    const inactive = total - active
    const activeRate = total > 0 ? Math.round((active / total) * 100) : 0

    return {
      total,
      active,
      inactive,
      activeRate
    }
  }

  const value = {
    sliderItems,
    loading,
    error,
    setSliderItems,
    fetchSlides,
    getActiveSlides,
    addSlide,
    updateSlide,
    deleteSlide,
    deleteSlides,
    toggleSlideActive,
    moveSlide,
    getStats
  }

  return (
    <SliderContext.Provider value={value}>
      {children}
    </SliderContext.Provider>
  )
}

export const useSlider = () => {
  const context = useContext(SliderContext)
  if (context === undefined) {
    throw new Error('useSlider must be used within a SliderProvider')
  }
  return context
}

export default SliderContext